/**
 * GlobalChatContext
 * 
 * This context provides shared chat state across the entire application.
 * It allows any component to access and control the chat system.
 */

import React, { createContext, useContext, useMemo } from 'react';
import { useGlobalChat, UseGlobalChatResult } from './useGlobalChat';
import type { GlobalChatProviderProps, GlobalChatContextValue, GlobalChatConfig } from './types';

// ============================================================================
// Context
// ============================================================================

const GlobalChatContext = createContext<GlobalChatContextValue | null>(null);

// ============================================================================
// Provider Component
// ============================================================================

/**
 * GlobalChatProvider wraps your application (or a section of it) to provide
 * shared chat state to all child components.
 * 
 * @example
 * ```tsx
 * // In your App.tsx or layout component
 * <GlobalChatProvider config={{ instanceId: 'main_chat' }}>
 *   <YourApp />
 * </GlobalChatProvider>
 * ```
 */
export const GlobalChatProvider: React.FC<GlobalChatProviderProps> = ({
  config,
  children,
}) => {
  const chatResult = useGlobalChat(config);

  const contextValue: GlobalChatContextValue = useMemo(() => ({
    state: chatResult.state,
    config: chatResult.config,
    dispatch: chatResult.dispatch,
    promptInputRef: chatResult.refs.promptInputRef,
    chatMessagesRef: chatResult.refs.chatMessagesRef,
    fileInputRef: chatResult.refs.fileInputRef,
    promptBarRef: chatResult.refs.promptBarRef,
    floatingPromptBarRef: chatResult.refs.floatingPromptBarRef,
    chatContainerRef: chatResult.refs.chatContainerRef,
    waitlistController: chatResult.waitlistController,
    handlers: chatResult.handlers,
  }), [chatResult]);

  return (
    <GlobalChatContext.Provider value={contextValue}>
      {children}
    </GlobalChatContext.Provider>
  );
};

// ============================================================================
// Hook to use the context
// ============================================================================

/**
 * useGlobalChatContext provides access to the shared chat state and handlers.
 * Must be used within a GlobalChatProvider.
 * 
 * @example
 * ```tsx
 * const { state, handlers } = useGlobalChatContext();
 * 
 * // Open the chat programmatically
 * handlers.handleOpen();
 * 
 * // Check if chat is open
 * console.log(state.isChatOpen);
 * ```
 */
export function useGlobalChatContext(): GlobalChatContextValue {
  const context = useContext(GlobalChatContext);
  
  if (!context) {
    throw new Error(
      'useGlobalChatContext must be used within a GlobalChatProvider. ' +
      'Wrap your component tree with <GlobalChatProvider> to use this hook.'
    );
  }
  
  return context;
}

// ============================================================================
// Optional: Hook that doesn't throw if outside provider
// ============================================================================

/**
 * useMaybeGlobalChatContext is similar to useGlobalChatContext but returns null
 * instead of throwing if used outside a provider. Useful for components that
 * may or may not be within a chat context.
 */
export function useMaybeGlobalChatContext(): GlobalChatContextValue | null {
  return useContext(GlobalChatContext);
}

// ============================================================================
// Convenience hooks for specific state slices
// ============================================================================

/**
 * Hook to get just the chat open state
 */
export function useIsChatOpen(): boolean {
  const context = useMaybeGlobalChatContext();
  return context?.state.isChatOpen ?? false;
}

/**
 * Hook to get just the messages
 */
export function useChatMessages() {
  const context = useMaybeGlobalChatContext();
  return context?.state.messages ?? [];
}

/**
 * Hook to get handlers for controlling the chat
 */
export function useChatHandlers() {
  const context = useMaybeGlobalChatContext();
  return context?.handlers ?? null;
}

// ============================================================================
// Higher-Order Component for class components
// ============================================================================

/**
 * withGlobalChat is a HOC that provides chat context to class components.
 * 
 * @example
 * ```tsx
 * class MyComponent extends React.Component<WithGlobalChatProps> {
 *   render() {
 *     const { globalChat } = this.props;
 *     return <button onClick={globalChat.handlers.handleOpen}>Open Chat</button>;
 *   }
 * }
 * 
 * export default withGlobalChat(MyComponent);
 * ```
 */
export interface WithGlobalChatProps {
  globalChat: GlobalChatContextValue;
}

export function withGlobalChat<P extends object>(
  WrappedComponent: React.ComponentType<P & WithGlobalChatProps>
) {
  return function WithGlobalChatWrapper(props: Omit<P, keyof WithGlobalChatProps>) {
    const chat = useGlobalChatContext();
    return <WrappedComponent {...(props as P)} globalChat={chat} />;
  };
}

export default GlobalChatContext;

